import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import * as Highcharts from 'highcharts';
import {chartConstructors} from './chart.utils';
import {KpiService} from '../../admin/kpi-overview/services/kpi.service';
import {forkJoin} from 'rxjs';
import {KpiHelper} from '../../admin/kpi-overview/helper';
import {Store} from '@ngrx/store';
import * as fromKpi from '../../admin/kpi-overview/reducers/kpi.reducers';
import {KpiRefreshService} from '../../admin/kpi-overview/services/kpi.refresh.service';

export interface ChartInterface {
  labels: Array<string | number>;
  dataSets: Array<any>;
}

@Component({
    selector: 'app-chart',
    templateUrl: './chart.component.html',
    styleUrls: ['./chart.component.scss'],
    standalone: false
})
export class ChartComponent extends KpiHelper implements OnChanges {
  @Input() data: ChartInterface;
  @Input() title: string;
  @Input() type: string;
  @Input() statName: Array<string>;
  @Input() xFormatter;
  @Input() yFormatter;
  @Input() tooltipFormatter;
  @Input() rangeSelector: boolean;
  @Input() nextMonthRangeAvailable: boolean;
  @Input() futureRanges: boolean;
  @Input() changed;
  @Input() noSpacer: boolean;
  @Input() countries: Array<string>;
  @Output() onCountrySelected = new EventEmitter();
  countrySelected;
  dataAvailable: boolean;

  seriesCount: number;
  rangeSelected;
  updateFlag;
  Highcharts: typeof Highcharts = Highcharts;
  chartOptions;

  constructor(
    private kpiService: KpiService,
    store: Store<fromKpi.KpiState>,
    refreshService: KpiRefreshService,
  ) {
    super(store, refreshService);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.dataAvailable = this.data && !!this.data.dataSets.length;
    if (this.dataAvailable) {
      this.drawChart();
    }

    if (changes.changed) {
      setTimeout(() => {
        this.changed = {refresh: false};
      });
    }
  }

  drawChart() {
    const data = this.deepCopy(this.data);

    this.chartOptions = new chartConstructors[this.type](data, this.title);
    if (this.xFormatter) {
      this.chartOptions.xAxis.labels.formatter = this.xFormatter;
    }

    if (this.yFormatter) {
      this.chartOptions.xAxis.labels.formatter = this.yFormatter;
    }

    if (this.tooltipFormatter) {
      this.chartOptions.tooltip.formatter = this.tooltipFormatter;
    }

    this.updateFlag = true;

    if (this.rangeSelector) {
      this.seriesCount = this.data.dataSets[0].data.length;
      this.setTimeRange(30);
    }
  }

  setTimeRange(range) {
    this.rangeSelected = range;
    const data = this.deepCopy(this.data);
    if (!range) {
      // range === null -> all_time
      const requests = [];
      this.statName.forEach(stat_name => {
        requests.push(this.kpiService.get(stat_name, 'all_time'));
      });
      forkJoin(requests).subscribe(res => {
        for (const [statNameIndex, dataSet] of res.entries()) {
          const uniqueData = this.removeDuplicates(dataSet);
          const preparedDataSet = this.prepareChartRangeData(null, uniqueData);
          this.prepareDataSets(preparedDataSet, data, statNameIndex);
          this.chartOptions.series = data.dataSets;
          this.updateFlag = true;
        }
      });
    } else if (range === 365) {
      data.dataSets.forEach(set => (set['data'] = set['data'].splice(0, range)));
    } else {
      data.dataSets.forEach(set => {
        range = range < set.data.length ? range : set.data.length;
        const dataSet = set['data'].splice(set['data'].length - range, set['data'].length);
        set['data'] = this.getDataSet(dataSet, range);
      });
    }
    this.chartOptions.series = data.dataSets;
    this.updateFlag = true;
  }

  getDataSet(dataSet, range) {
    const today = Date.now();
    return dataSet.filter(timestamp => {
      if (!timestamp) {
        return false;
      }
      const daysDifference = (today - timestamp[0]) / (1000 * 60 * 60 * 24); // 1000 * 60 * 60 * 24 = 24 hours
      return daysDifference <= range;
    });
  }

  prepareDataSets(preparedDataSet, data, statNameIndex: number) {
    // important for multiple data series for one stat_name: e.g. 'daily-tickets-sold-by-affiliate'
    for (let dataSerieIndex = 0; dataSerieIndex < preparedDataSet.dataSets.length; dataSerieIndex++) {
      data.dataSets[statNameIndex + dataSerieIndex].data = preparedDataSet.dataSets[dataSerieIndex].data;
    }
  }

  setCountry(country) {
    this.countrySelected = country;
    this.onCountrySelected.emit(country);
  }

  deepCopy = data => JSON.parse(JSON.stringify(data));
}
